// frontend/src/pages/auth/LoginPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, Lock, AlertCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import useAuthStore from '../../store/authStore';

const loginSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  rememberMe: yup.boolean(),
});

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [savedEmail, setSavedEmail] = useState('');
  const navigate = useNavigate();
  const { login, isLoading, error } = useAuthStore();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });

  // Load saved email on mount
  useEffect(() => {
    const saved = localStorage.getItem('savedEmail');
    if (saved) {
      setSavedEmail(saved);
      setValue('email', saved);
      setValue('rememberMe', true);
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    try {
      const result = await login(data);
      
      // If login successful and rememberMe is checked, save email
      if (data.rememberMe && result?.success !== false) {
        localStorage.setItem('savedEmail', data.email);
      } else if (!data.rememberMe) {
        localStorage.removeItem('savedEmail');
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  // Watch rememberMe to handle email save/remove
  const rememberMe = watch('rememberMe');

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to home
        </Link>

        {/* Hero Image Card with Overlay */}
        <div className="relative rounded-2xl overflow-hidden mb-6 shadow-lg">
          <img 
            src="/homepage-login-images/hero-login.png" 
            alt="Fastel App"
            className="w-full h-64 object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          
          {/* Content Overlay - Bottom aligned with glass effect */}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/fastel-logo.png" 
                alt="Fastel" 
                className="h-10 w-10 rounded-xl shadow-lg"
              />
              <div>
                <h2 className="text-white font-bold text-lg">
                  Welcome to Fastel
                </h2>
                <p className="text-white/80 text-sm">
                  Fast, reliable VTU & bill payments
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <div className="space-y-5">
          <div>
            <div className="flex items-center mb-1">
              <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
              <h2 className="text-xl font-bold text-gray-800">Welcome back!</h2>
            </div>
            <p className="text-sm text-gray-500 ml-3">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-primary-600 hover:text-primary-700">
                Sign up
              </Link>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
              <div className="flex items-start">
                <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-red-800">Error</h3>
                  <p className="text-xs text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register('email')}
                  className={`block w-full pl-9 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                    errors.email ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                  }`}
                  placeholder="you@example.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password')}
                  className={`block w-full pl-9 pr-10 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                    errors.password ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  {...register('rememberMe')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded shadow-neomorph-inset cursor-pointer"
                />
                <span className="ml-2 text-xs text-gray-600">Remember me</span>
              </label>

              <Link
                to="/forgot-password"
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                Forgot password?
              </Link>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-3 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          {/* Version Info */}
          <div className="text-center mt-4">
            <p className="text-xs text-gray-400">Fastel v1.0.0</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;